import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import HeaderBar from '../CrystalContainer/HeaderBar';
import { useCaptureProfiles, useSelectedImagingSchedule, useTableSort } from './hooks';
import './Imaging.scss';
import { FmlxColumn } from 'business/models/CustomModels';
import {
  ImagingSchedulesDTO,
  ImagingSchedulesDeletedDataDTO,
  ImagingSchedulesEntryDTO,
} from 'business/models/Models';
import {
  addEntryCaptureProfile,
  getRandomNegativeNumber,
  removeEntryCaptureProfile,
} from './utils';
import { ScheduleRowInfo } from './types';
import { sortTableRows } from 'utils/Global';
import { setImagingSchedulesIsDirty } from 'redux/reducers/imagingSchedulesReducer';
import { useDispatch } from 'react-redux';
import { useImagingSchedulesIsDirty } from 'redux/hooks/imagingSetup';
import { RMModalProps } from 'components/modal';
import { FmlxTable } from 'fmlx-common-ui';
import { Element } from 'constants/Element';
import { NoTableDataPlaceholder } from './NoTableDataPlaceholder';
import { withFmlxRowErrors } from 'components/customComponents/Controls/withFmlxRowErrors';

interface ScheduleDetailsProps {
  imagingSchedules: ImagingSchedulesDTO[];
  setImagingScheduleDto: React.Dispatch<React.SetStateAction<ImagingSchedulesDTO[]>>;
  setDeletedData: React.Dispatch<React.SetStateAction<ImagingSchedulesDeletedDataDTO>>;
  scheduleErrors?: ScheduleRowInfo;
  setModal?: React.Dispatch<React.SetStateAction<RMModalProps>>;
}

function ScheduleDetails(props: ScheduleDetailsProps) {
  const { imagingSchedules, setImagingScheduleDto, setDeletedData, scheduleErrors } =
    props;
  const selectedImgSchId = useSelectedImagingSchedule();
  const { captureProfiles } = useCaptureProfiles();
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [isEditState, setEditState] = useState(false);
  const [editingIds, setEditingIds] = useState<number[]>([]);
  const { sortState, onTableSorted } = useTableSort();
  const isDirty = useImagingSchedulesIsDirty();
  const dispatch = useDispatch();
  const tableContainerRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const setInputRef = useCallback((input: HTMLInputElement | null) => {
    inputRef.current = input;
    input?.focus();
  }, []);

  const onCellChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>, id: number, cpId?: number) => {
      const { name, value, type } = e.target;
      setImagingScheduleDto((prev) => {
        return prev.map((schedule) => {
          if (schedule.id === selectedImgSchId) {
            const updatedEntries = schedule.imagingScheduleEntry.map((entry) => {
              if (entry.id === id) {
                if (type === 'checkbox' && name === 'notify') {
                  return new ImagingSchedulesEntryDTO({
                    ...entry,
                    [name]: !entry[name],
                  });
                } else if (type === 'checkbox' && name === 'captureProfile') {
                  const updatedEntry = new ImagingSchedulesEntryDTO(entry);
                  entry.imagingScheduleEntryCaptureProfile.some(
                    (profile) => profile.captureProfileId === cpId,
                  )
                    ? removeEntryCaptureProfile(updatedEntry, cpId, setDeletedData)
                    : addEntryCaptureProfile(updatedEntry, cpId);
                  return updatedEntry;
                } else if (type === 'number') {
                  return new ImagingSchedulesEntryDTO({
                    ...entry,
                    [name]: parseInt(value, 10),
                  });
                } else {
                  return new ImagingSchedulesEntryDTO({
                    ...entry,
                    [name]: value,
                  });
                }
              }
              return entry;
            });

            return new ImagingSchedulesDTO({
              ...schedule,
              imagingScheduleEntry: updatedEntries,
            });
          }
          return schedule;
        });
      });

      dispatch(setImagingSchedulesIsDirty(true));
    },
    [[imagingSchedules, selectedImgSchId]],
  );

  const ScheduleDetailsColumns: FmlxColumn[] = [
    {
      Header: 'Days',
      accessor: 'days',
      filterDefinition: { type: FmlxTable.FilterType.TEXT },
      editable: false,
      disableSort: false,
      hideSort: false,
      hideFilter: true,
      headerStyle: {},
    },
    {
      Header: 'Hours',
      accessor: 'hours',
      filterDefinition: { type: FmlxTable.FilterType.TEXT },
      editable: false,
      disableSort: false,
      hideSort: false,
      hideFilter: true,
      headerStyle: {},
    },
    {
      Header: 'Notify',
      accessor: 'notify',
      filterDefinition: { type: FmlxTable.FilterType.TEXT },
      editable: false,
      disableSort: true,
      hideSort: true,
      hideFilter: true,
      headerStyle: {},
      Cell: ({ value, row }) => (
        <input
          id="scheduledetails-checkbox-notify"
          type="checkbox"
          name="notify"
          checked={value === true}
          className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
          onChange={(e) => onCellChange(e, row.original.id)}
          onClick={(e) => e.stopPropagation()}
        />
      ),
    },
    {
      Header: 'Priority',
      accessor: 'priority',
      filterDefinition: { type: FmlxTable.FilterType.TEXT },
      editable: false,
      disableSort: false,
      hideSort: false,
      hideFilter: true,
      headerStyle: {},
    },
    ...(captureProfiles || []).map((cp) => ({
      Header: cp.name,
      accessor: cp.id.toString(),
      filterDefinition: { type: FmlxTable.FilterType.TEXT },
      editable: false,
      disableSort: true,
      hideSort: true,
      hideFilter: true,
      headerStyle: {},
      Cell: ({ row }) => {
        return (
          <input
            id="scheduledetails-checkbox-captureProfile"
            type="checkbox"
            name="captureProfile"
            checked={row.original.imagingScheduleEntryCaptureProfile.some(
              (d) => d.captureProfileId === cp.id,
            )}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => onCellChange(e, row.original.id, cp.id)}
            className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
          />
        );
      },
    })),
  ];

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>, id: number) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      setEditingIds((prev) => prev.filter((editingId) => editingId !== id));
    }
  };

  const renderDaysHoursPriority = (id: number, name: string, value: any) => {
    return !editingIds.includes(id) ? (
      <div
        id="scheduledetails-renderdayshourspriority-value"
        className="!border-0 bg-transparent p-2 w-full min-h-[2rem]"
      >
        {value}
      </div>
    ) : (
      <input
        key={id}
        id="scheduledetails-renderdayshourspriority-input"
        type="number"
        name={name}
        value={value}
        min={0}
        className="bg-white p-2 w-full"
        style={{ border: '1px solid black', borderRadius: '0.25rem' }}
        onChange={(e) => onCellChange(e, id)}
        onClick={(e) => e.stopPropagation()}
        ref={name === 'days' ? setInputRef : undefined}
        onKeyDown={(e) => handleKeyPress(e, id)}
      />
    );
  };

  const entryErrors = useMemo(() => {
    if (!scheduleErrors || !imagingSchedules || !selectedImgSchId) {
      return undefined;
    }
    const index = imagingSchedules.findIndex((x) => x.id === selectedImgSchId);
    return index >= 0 ? scheduleErrors[index]?.entryErrors : undefined;
  }, [scheduleErrors, imagingSchedules, selectedImgSchId]);

  const tableData = useMemo(() => {
    const imagingScheduleEntries = imagingSchedules?.find(
      (x) => x.id === selectedImgSchId,
    )?.imagingScheduleEntry;
    if (imagingScheduleEntries) {
      const sortedEntries = sortTableRows(imagingScheduleEntries, sortState);
      return sortedEntries.map((entry: ImagingSchedulesEntryDTO) => ({
        id: entry.id,
        days: renderDaysHoursPriority(entry.id, 'days', entry.days),
        hours: renderDaysHoursPriority(entry.id, 'hours', entry.hours),
        notify: entry.notify,
        priority: renderDaysHoursPriority(entry.id, 'priority', entry.priority),
        imagingScheduleEntryCaptureProfile: entry.imagingScheduleEntryCaptureProfile,
      }));
    }
    return [];
  }, [imagingSchedules, selectedImgSchId, editingIds, sortState]);

  const handleOnRowClick = (row) => {
    const rowIndex = tableData.findIndex((item) => item.id === row.id);
    setSelectedIds(rowIndex >= 0 ? [rowIndex] : []);
    if (isEditState && row.id !== undefined) {
      setEditingIds((prev) => (prev.includes(row.id) ? prev : [...prev, row.id]));
    }
  };

  const onAdd = () => {
    const rndNum = getRandomNegativeNumber();
    const imagingScheduleEntries = imagingSchedules?.find(
      (x) => x.id === selectedImgSchId,
    )?.imagingScheduleEntry;
    const newData = [...(imagingScheduleEntries ?? [])];
    const newDataItem = new ImagingSchedulesEntryDTO({
      id: rndNum,
      days: 0,
      hours: 0,
      notify: true,
      priority: newData?.length + 1,
      imagingScheduleEntryCaptureProfile: [],
    });
    newData.push(newDataItem);
    setImagingScheduleDto((prev) => {
      return prev.map((schedule) => {
        if (schedule.id === selectedImgSchId) {
          const updatedSchedule = {
            ...schedule,
            imagingScheduleEntry: [...newData],
          };
          return new ImagingSchedulesDTO(updatedSchedule);
        }
        return schedule;
      });
    });
    setEditingIds((prev) => [...prev, rndNum]);
    dispatch(setImagingSchedulesIsDirty(true));
  };

  const onEdit = () => {
    setEditState((prev) => !prev);
    setEditingIds((prev) => (isEditState ? [] : prev));
  };

  useEffect(() => {
    const handleOutsideTableClick = (event: MouseEvent) => {
      const target = event.target as Element;
      const clickedEditButton = target.closest(Element.EDIT_BUTTON_SELECTOR);

      if (
        isEditState &&
        !clickedEditButton &&
        tableContainerRef.current &&
        !tableContainerRef.current.contains(target)
      ) {
        setEditState(false);
        setEditingIds([]);
      }
    };

    document.addEventListener('mousedown', handleOutsideTableClick);
    return () => document.removeEventListener('mousedown', handleOutsideTableClick);
  }, [isEditState]);

  const onDelete = async () => {
    const ids = selectedIds
      .map((i) => tableData[i]?.id)
      .filter((id): id is number => id !== undefined);
    const existingIds = ids.filter((id) => id > 0);
    setImagingScheduleDto((prev) => {
      return prev.map((schedule) => {
        if (schedule.id === selectedImgSchId) {
          const updatedEntry = schedule.imagingScheduleEntry.filter(
            (x) => !ids.includes(x.id),
          );
          return new ImagingSchedulesDTO({
            ...schedule,
            imagingScheduleEntry: updatedEntry,
          });
        }
        return schedule;
      });
    });

    setDeletedData(
      (prevData) =>
        new ImagingSchedulesDeletedDataDTO({
          ...prevData,
          imagingScheduleEntryIds: Array.from(
            new Set([...(prevData?.imagingScheduleEntryIds || []), ...existingIds]),
          ),
        }),
    );
    dispatch(setImagingSchedulesIsDirty(true));
    setSelectedIds([0]);
  };

  useEffect(() => {
    if (!isDirty) {
      setEditingIds([]);
      setEditState(false);
    }
  }, [isDirty]);

  return (
    <div
      id="scheduledetails-wrapper"
      className="flex h-full min-h-0 flex-col overflow-hidden"
    >
      <HeaderBar
        title="Schedule Details"
        onAdd={onAdd}
        onEdit={onEdit}
        onDelete={() => onDelete()}
        activeEdit={isEditState}
        outlinedEdit
        enableEditDelete={!!selectedImgSchId}
        disableAdd={!selectedImgSchId}
      />
      <div ref={tableContainerRef} className="relative min-h-0 flex-1 overflow-hidden">
        <FmlxTable
          id="schedule-data-table"
          columns={withFmlxRowErrors(ScheduleDetailsColumns, (row) => {
            const entries = imagingSchedules?.find(
              (schedule) => schedule.id === selectedImgSchId,
            )?.imagingScheduleEntry;
            const index = entries?.findIndex((entry) => entry.id === row.id) ?? -1;
            return index >= 0 ? entryErrors?.[index]?.msg : undefined;
          })}
          data={tableData}
          onRowClick={handleOnRowClick}
          getSortValue={onTableSorted}
          placeholder={null}
        />
        {tableData.length === 0 && <NoTableDataPlaceholder />}
      </div>
    </div>
  );
}

export default ScheduleDetails;
